import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, Pencil, Trash2, Send } from "lucide-react";

function Conversation({ messages, date }) {
  const { t } = useLang();
  return (
    <div className="mt-4 space-y-2">
      {messages.map((m, i) => (
        <div key={i} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[80%] rounded-sm px-4 py-2 text-sm ${m.sender === "admin" ? "bg-black text-white" : "bg-neutral-100 text-neutral-800"}`}>
            <p className="text-[10px] uppercase tracking-widest opacity-60">{m.sender === "admin" ? "CH CREATIVE" : t("contact.from")} · {date(m.created_at)}</p>
            <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminContact() {
  const { t, date } = useLang();
  const [threads, setThreads] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/contact").then((r) => setThreads(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.post("/contact/read-all").then(() => window.dispatchEvent(new Event("contact-updated"))).catch(() => {});
  }, [load]);

  const send = async (thread) => {
    const body = (drafts[thread.id] || "").trim();
    if (!body) return toast.error(t("contact.reply_empty"));
    try {
      await api.post(`/contact/${thread.id}/message`, { body });
      toast.success(t("contact.reply_sent"));
      setDrafts((d) => ({ ...d, [thread.id]: "" }));
      window.dispatchEvent(new Event("contact-updated"));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/contact/${editing.id}`, { subject: editing.subject });
      toast.success(t("contact.msg_updated"));
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (thread) => {
    if (!window.confirm(t("contact.del_confirm"))) return;
    await api.delete(`/contact/${thread.id}`);
    toast.success(t("contact.msg_deleted"));
    window.dispatchEvent(new Event("contact-updated"));
    load();
  };

  return (
    <div className="ch-fade-up space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("contact.eyebrow_admin")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("nav.contact")}</h1>
        <p className="mt-2 text-sm text-neutral-500">{t("contact.subtitle_admin")}</p>
      </header>

      {threads.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-sm border border-dashed border-neutral-200 py-16 text-neutral-400">
          <MessageSquare className="h-8 w-8" strokeWidth={1.5} />
          <p className="text-sm">{t("contact.empty_admin")}</p>
        </div>
      )}

      <div className="space-y-4">
        {threads.map((thread) => (
          <div key={thread.id} data-testid={`contact-thread-${thread.id}`} className="rounded-sm border border-neutral-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display text-lg font-bold">{thread.subject}</p>
                <p className="text-sm text-neutral-500">{thread.client_name}</p>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-thread-${thread.id}`} onClick={() => setEditing({ id: thread.id, subject: thread.subject })} className="rounded-sm p-1.5 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-thread-${thread.id}`} onClick={() => del(thread)} className="rounded-sm p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <Conversation messages={thread.messages} date={date} />
            <div className="mt-4 flex gap-2">
              <Textarea data-testid={`reply-input-${thread.id}`} value={drafts[thread.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [thread.id]: e.target.value }))}
                placeholder={t("contact.reply_placeholder")} className="rounded-sm" rows={1} />
              <Button data-testid={`send-reply-${thread.id}`} onClick={() => send(thread)} className="shrink-0 gap-2 rounded-sm bg-black hover:bg-neutral-800">
                <Send className="h-4 w-4" /> {t("contact.send")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-thread-dialog" className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("contact.edit_msg")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-1.5"><Label>{t("contact.subject")}</Label><Input data-testid="edit-subject" value={editing.subject} onChange={(e) => setEditing((s) => ({ ...s, subject: e.target.value }))} className="rounded-sm" /></div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button data-testid="save-thread-button" onClick={saveEdit} className="rounded-sm bg-black hover:bg-neutral-800">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
